import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  MessageCircle,
  Globe,
  Facebook,
  Instagram,
  Twitter,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { DashboardLayout } from '../../components/DashboardLayout';

const API_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL);

const ContactUs = () => {
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contact-info`);
      if (response.ok) {
        const data = await response.json();
        setContactInfo(data);
      }
    } catch (error) {
      console.error('Error fetching contact info:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneForCall = (phone) => {
    return phone.replace(/[^0-9+]/g, '');
  };

  const formatPhoneForWhatsApp = (phone) => {
    // Remove all non-numeric characters except +
    let cleaned = phone.replace(/[^0-9+]/g, '');
    // Remove leading + if present, as WhatsApp API prefers without
    if (cleaned.startsWith('+')) {
      cleaned = cleaned.substring(1);
    }
    return cleaned;
  };

  const getDaySchedule = (schedule, day) => {
    if (!schedule || !schedule[day]) return null;
    const daySchedule = schedule[day];
    if (daySchedule.closed) return 'Closed';
    return `${daySchedule.open} - ${daySchedule.close}`;
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = {
    monday: 'Monday',
    tuesday: 'Tuesday', 
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday'
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="contact-us-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Contact Us</h1>
            <p className="text-slate-600 mt-1">Get in touch with {contactInfo?.business_name || 'the academy'}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Contact Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-blue-500" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Phone */}
              {contactInfo?.business_phone && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Phone className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500 font-medium">Phone</p>
                    <p className="text-lg text-slate-900">{contactInfo.business_phone}</p>
                    <Button 
                      variant="link" 
                      className="p-0 h-auto text-blue-600"
                      onClick={() => window.open(`tel:${formatPhoneForCall(contactInfo.business_phone)}`)}
                      data-testid="call-btn"
                    >
                      Call Now →
                    </Button>
                  </div>
                </div>
              )}

              {/* WhatsApp */}
              {contactInfo?.whatsapp_number && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <MessageCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500 font-medium">WhatsApp</p>
                    <p className="text-lg text-slate-900">{contactInfo.whatsapp_number}</p>
                    <Button 
                      variant="link" 
                      className="p-0 h-auto text-green-600"
                      onClick={() => window.open(`https://wa.me/${formatPhoneForWhatsApp(contactInfo.whatsapp_number)}`, '_blank')}
                      data-testid="whatsapp-btn"
                    >
                      Message on WhatsApp →
                    </Button>
                  </div>
                </div>
              )}

              {/* Email */}
              {contactInfo?.email && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Mail className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500 font-medium">Email</p>
                    <p className="text-lg text-slate-900">{contactInfo.email}</p>
                    <Button 
                      variant="link" 
                      className="p-0 h-auto text-purple-600"
                      onClick={() => window.open(`mailto:${contactInfo.email}`)}
                      data-testid="email-btn"
                    >
                      Send Email →
                    </Button>
                  </div>
                </div>
              )}

              {/* Address */}
              {contactInfo?.address && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <MapPin className="h-5 w-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500 font-medium">Address</p>
                    <p className="text-lg text-slate-900">{contactInfo.address}</p>
                    <Button 
                      variant="link" 
                      className="p-0 h-auto text-orange-600"
                      onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(contactInfo.address)}`, '_blank')}
                      data-testid="directions-btn"
                    >
                      Get Directions →
                    </Button>
                  </div>
                </div>
              )}

              {/* Website */}
              {contactInfo?.website && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-slate-100 rounded-lg">
                    <Globe className="h-5 w-5 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500 font-medium">Website</p>
                    <p className="text-lg text-slate-900">{contactInfo.website}</p>
                    <Button 
                      variant="link" 
                      className="p-0 h-auto text-slate-600"
                      onClick={() => window.open(contactInfo.website.startsWith('http') ? contactInfo.website : `https://${contactInfo.website}`, '_blank')}
                      data-testid="website-btn"
                    >
                      Visit Website <ExternalLink className="h-3 w-3 ml-1 inline" />
                    </Button>
                  </div>
                </div>
              )}

              {/* No contact info message */}
              {!contactInfo?.business_phone && !contactInfo?.whatsapp_number && !contactInfo?.email && (
                <div className="text-center py-8 text-slate-500">
                  <Phone className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Contact information not yet configured.</p>
                  <p className="text-sm">Please check back later.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Business Hours Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                Business Hours
                {contactInfo?.business_hours?.season_name && (
                  <Badge variant="outline" className="ml-2">
                    {contactInfo.business_hours.season_name}
                  </Badge>
                )}
              </CardTitle>
              {contactInfo?.business_hours?.start_date && contactInfo?.business_hours?.end_date && (
                <p className="text-sm text-slate-500 mt-1">
                  {new Date(contactInfo.business_hours.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {new Date(contactInfo.business_hours.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              )}
            </CardHeader>
            <CardContent>
              {contactInfo?.business_hours?.schedule ? (
                <div className="space-y-3">
                  {days.map(day => {
                    const schedule = getDaySchedule(contactInfo.business_hours.schedule, day);
                    const isToday = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() === day;
                    const isClosed = schedule === 'Closed';
                    
                    return (
                      <div 
                        key={day} 
                        className={`flex justify-between items-center py-2 px-3 rounded-lg ${
                          isToday ? 'bg-blue-50 border border-blue-200' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${isToday ? 'text-blue-700' : 'text-slate-700'}`}>
                            {dayLabels[day]}
                          </span>
                          {isToday && (
                            <Badge className="bg-blue-500 text-white text-xs">Today</Badge>
                          )}
                        </div>
                        <span className={`${
                          isClosed ? 'text-red-500' : isToday ? 'text-blue-700 font-medium' : 'text-slate-600'
                        }`}>
                          {schedule || 'Not set'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Business hours not yet configured.</p>
                  <p className="text-sm">Please contact us for availability.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Social Media Card */}
          {(contactInfo?.social_links?.facebook || contactInfo?.social_links?.instagram || contactInfo?.social_links?.twitter) && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Follow Us</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  {contactInfo.social_links.facebook && (
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                      onClick={() => window.open(contactInfo.social_links.facebook, '_blank')}
                      data-testid="facebook-btn"
                    >
                      <Facebook className="h-5 w-5 text-blue-600" />
                      Facebook
                    </Button>
                  )}
                  {contactInfo.social_links.instagram && (
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                      onClick={() => window.open(contactInfo.social_links.instagram, '_blank')}
                      data-testid="instagram-btn"
                    >
                      <Instagram className="h-5 w-5 text-pink-600" />
                      Instagram
                    </Button>
                  )}
                  {contactInfo.social_links.twitter && (
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                      onClick={() => window.open(contactInfo.social_links.twitter, '_blank')}
                      data-testid="twitter-btn"
                    >
                      <Twitter className="h-5 w-5 text-sky-500" />
                      Twitter
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ContactUs;
